/**
 * CollapsiblePageHeader — a drop-in replacement for `@primer/react`'s
 * `PageHeader` that auto-includes a sidebar trigger in its `LeadingAction`
 * slot on narrow viewports.
 *
 * Built to pair with `SidebarPageLayout`/`FilteredListPageLayout`: on the
 * narrow viewport where the sidebar rail is hidden behind a dialog, the
 * trigger needs a home in the page header. This wrapper provides exactly
 * that, with no introspection or `cloneElement` plumbing required.
 *
 * ## Opt out
 *
 * If your header needs a custom `LeadingAction` (and not the auto-included
 * sidebar trigger), drop back to the raw `<PageHeader>` from `@primer/react`
 * and render a `<SidebarPageLayout.SidebarTrigger />` (or
 * `<CollapsibleSidebar.Toggle />`) wherever it fits.
 *
 * ## Sub-components
 *
 * All `PageHeader.*` sub-components are re-exported as
 * `CollapsiblePageHeader.*` via direct reference assignment, so each one
 * preserves its primer `__SLOT__` symbol identity for free.
 */
import {PageHeader} from '@primer/react'
import {use, type ComponentProps, type PropsWithChildren} from 'react'

import {SidebarTrigger} from './SidebarTrigger'
import {SidebarPageLayoutStateContext} from './state'

export type CollapsiblePageHeaderProps = ComponentProps<typeof PageHeader>

// Hide the sidebar trigger on viewports where the sidebar rail itself is
// visible — duplicating the affordance would be confusing.
const TRIGGER_HIDDEN_ON_DESKTOP = {narrow: false, regular: true, wide: true} as const

function CollapsiblePageHeader({children, ...props}: PropsWithChildren<CollapsiblePageHeaderProps>) {
  const {hasSidebar} = use(SidebarPageLayoutStateContext)

  return (
    <PageHeader {...props}>
      {hasSidebar ? (
        <PageHeader.LeadingAction hidden={TRIGGER_HIDDEN_ON_DESKTOP}>
          <SidebarTrigger />
        </PageHeader.LeadingAction>
      ) : null}
      {children}
    </PageHeader>
  )
}

CollapsiblePageHeader.displayName = 'CollapsiblePageHeader'

// Reference re-exports — each sub-component keeps its primer `__SLOT__`
// symbol identity, so primer's internal slot detection treats them
// identically to a raw `<PageHeader.X>`.
const CollapsiblePageHeaderCompound = Object.assign(CollapsiblePageHeader, {
  ContextArea: PageHeader.ContextArea,
  ParentLink: PageHeader.ParentLink,
  ContextBar: PageHeader.ContextBar,
  TitleArea: PageHeader.TitleArea,
  ContextAreaActions: PageHeader.ContextAreaActions,
  LeadingAction: PageHeader.LeadingAction,
  Breadcrumbs: PageHeader.Breadcrumbs,
  LeadingVisual: PageHeader.LeadingVisual,
  Title: PageHeader.Title,
  TrailingVisual: PageHeader.TrailingVisual,
  TrailingAction: PageHeader.TrailingAction,
  Actions: PageHeader.Actions,
  Description: PageHeader.Description,
  Navigation: PageHeader.Navigation,
})

export {CollapsiblePageHeaderCompound as CollapsiblePageHeader}
